import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Trash2, X, Mail, Phone, Building2, MapPin, FileText, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';

const emptyForm = {
  customer_type: 'company',
  company_name: '',
  contact_person: '',
  org_number: '',
  email: '',
  phone: '',
  billing_address: '',
  postal_code: '',
  city: '',
  country: 'Sverige',
  notes: '',
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);

  useEffect(() => { fetchCustomers(); }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await api.listInvoiceCustomers();
      setCustomers(res.data || []);
    } catch (err) {
      toast.error('Kunde inte hämta kunder');
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    const lower = search.toLowerCase();
    if (!lower) return customers;
    return customers.filter((c) =>
      (c.company_name || '').toLowerCase().includes(lower) ||
      (c.contact_person || '').toLowerCase().includes(lower) ||
      (c.email || '').toLowerCase().includes(lower) ||
      (c.org_number || '').toLowerCase().includes(lower) ||
      (c.city || '').toLowerCase().includes(lower)
    );
  }, [customers, search]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      customer_type: c.customer_type || 'company',
      company_name: c.company_name || '',
      contact_person: c.contact_person || '',
      org_number: c.org_number || '',
      email: c.email || '',
      phone: c.phone || '',
      billing_address: c.billing_address || '',
      postal_code: c.postal_code || '',
      city: c.city || '',
      country: c.country || 'Sverige',
      notes: c.notes || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.company_name) { toast.error('Företagsnamn krävs'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.updateInvoiceCustomer(editing.id, form);
        toast.success('Kund uppdaterad');
      } else {
        await api.createInvoiceCustomer(form);
        toast.success('Kund skapad');
      }
      setShowForm(false);
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte spara kund');
    } finally { setSaving(false); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Radera kund "${c.company_name}"?`)) return;
    try {
      await api.deleteInvoiceCustomer(c.id);
      toast.success('Kund raderad');
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte radera kund');
    }
  };

  const handleMigrate = async () => {
    setMigrating(true);
    try {
      const res = await api.migrateImportedCustomers();
      toast.success(`${res.data.created} kunder importerade till registret (${res.data.skipped} fanns redan)`);
      fetchCustomers();
    } catch (err) {
      toast.error('Kunde inte importera kunder');
    } finally { setMigrating(false); }
  };

  return (
    <div className="space-y-6 max-w-7xl" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 size={22} className="text-[#E8613A]" /> Kunder
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {customers.length} kunder i registret. Här kan du skapa, redigera och radera kunder som du fakturerar.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handleMigrate}
            disabled={migrating}
            className="text-xs"
            data-testid="customers-migrate-btn"
          >
            <Download size={14} className="mr-1.5" /> {migrating ? 'Importerar…' : 'Importera från Fortnox/Bokio'}
          </Button>
          <Button
            onClick={openCreate}
            className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full"
            data-testid="customers-new-btn"
          >
            <Plus size={14} className="mr-1.5" /> Ny kund
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Sök på namn, e-post, org.nr eller stad…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="customers-search-input"
        />
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground text-sm" data-testid="customers-loading">
            Laddar kunder…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm" data-testid="customers-empty">
            {search ? 'Inga kunder matchar sökningen' : 'Inga kunder ännu — klicka på "Ny kund" eller importera från Fortnox/Bokio'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-5 py-3">Kund</th>
                  <th className="text-left px-3 py-3">Org.nr</th>
                  <th className="text-left px-3 py-3">Kontakt</th>
                  <th className="text-left px-3 py-3">Stad</th>
                  <th className="text-right px-5 py-3">Åtgärd</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-t border-border hover:bg-muted/30" data-testid={`customer-row-${c.id}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-foreground">{c.company_name}</div>
                      {c.contact_person && <div className="text-xs text-muted-foreground mt-0.5">{c.contact_person}</div>}
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">{c.org_number || '—'}</td>
                    <td className="px-3 py-3 text-muted-foreground">
                      {c.email && <div className="flex items-center gap-1.5 text-xs"><Mail size={11} /> {c.email}</div>}
                      {c.phone && <div className="flex items-center gap-1.5 text-xs mt-0.5"><Phone size={11} /> {c.phone}</div>}
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">
                      {c.city ? <div className="flex items-center gap-1.5 text-xs"><MapPin size={11} /> {c.city}</div> : '—'}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => openEdit(c)}
                          className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                          data-testid={`customer-edit-${c.id}`}
                          title="Redigera"
                        >
                          <Edit2 size={13} />
                        </button>
                        <button
                          onClick={() => handleDelete(c)}
                          className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 dark:hover:bg-red-950"
                          data-testid={`customer-delete-${c.id}`}
                          title="Radera"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit / Create dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Redigera kund' : 'Ny kund'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="customer-form">
            <div className="sm:col-span-2">
              <Label className="text-xs">Företagsnamn *</Label>
              <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                className="mt-1" data-testid="customer-company-name" />
            </div>
            <div>
              <Label className="text-xs">Kontaktperson</Label>
              <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Organisationsnummer</Label>
              <Input value={form.org_number} onChange={(e) => setForm({ ...form, org_number: e.target.value })} className="mt-1" placeholder="556789-1234" />
            </div>
            <div>
              <Label className="text-xs">E-post</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Telefon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Fakturaadress</Label>
              <Input value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Postnummer</Label>
              <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Stad</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Anteckningar</Label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="mt-1 w-full text-sm rounded-md border border-border bg-background px-3 py-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Avbryt</Button>
            <Button onClick={handleSave} disabled={saving}
              className="bg-[#E8613A] hover:bg-[#D4532F] text-white"
              data-testid="customer-save-btn">
              {saving ? 'Sparar…' : editing ? 'Spara ändringar' : 'Skapa kund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
